import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface TopbarProps {
  sidebarOpen?: boolean;
  setSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Topbar({
  sidebarOpen = true,
  setSidebarOpen,
}: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const { user, signOut, currentOrganization } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user's display name or email
  const displayName =
    user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User";

  // Get first letter for avatar
  const avatarLetter = (
    user?.user_metadata?.full_name?.charAt(0) ||
    user?.email?.charAt(0) ||
    "U"
  ).toUpperCase();

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Determine the current page title based on the path
  const getPageTitle = () => {
    const path = location.pathname;
    const pathParts = path.split("/");

    // Handle organization path structure: /org/:orgId/...
    if (pathParts[1] === "org" && pathParts.length > 3) {
      const section = pathParts[3];

      // Handle specific cases
      if (section === "inventory" && pathParts.length > 4) {
        return "Inventory Detail";
      }

      if (section === "settings") {
        return "Organization Settings";
      }

      // Default case for organization sections
      if (section) {
        return section.charAt(0).toUpperCase() + section.slice(1);
      }

      return "Dashboard";
    }

    // Handle non-organization paths
    if (path === "/profile") return "My Profile";
    if (path === "/organizations/create") return "Create Organization";

    return "Dashboard";
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Failed to sign out", error);
    }
  };

  const toggleSidebar = () => {
    if (setSidebarOpen) {
      setSidebarOpen(!sidebarOpen);
    }
  };

  return (
    <header className="h-16 border-b border-gray-200 px-4 md:px-6 flex items-center justify-between bg-white shadow-sm sticky top-0 z-10">
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          onClick={toggleSidebar}
          className="text-gray-500 hover:text-gray-700 focus:outline-none md:hidden"
          aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {sidebarOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        <div className="flex items-center">
          <h1 className="text-xl font-semibold tracking-tight text-dark">
            {getPageTitle()}
          </h1>
          {currentOrganization && (
            <span className="ml-3 text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
              Development
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3 relative" ref={dropdownRef}>
          <div className="text-sm text-gray-500 hidden sm:flex items-center">
            <span>Logged in as:</span>
            <span className="font-medium text-dark ml-1">{displayName}</span>
          </div>

          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="relative flex items-center focus:outline-none group"
            aria-label="Open user menu"
          >
            <div className="flex items-center p-1.5 rounded-full transition-all duration-200 group-hover:bg-gray-100 group-active:bg-gray-200">
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="Profile"
                  className="w-9 h-9 rounded-full object-cover border border-gray-200 shadow-sm"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium shadow-sm">
                  {avatarLetter}
                </div>
              )}
              <svg
                className="w-4 h-4 ml-1.5 text-gray-500 group-hover:text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Touch-friendly ripple effect indicator */}
            <span className="absolute inset-0 rounded-full pointer-events-none group-active:bg-gray-200 group-active:opacity-30 transition-opacity duration-300"></span>
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-lg shadow-lg py-1 z-20 border border-gray-200 overflow-hidden">
              {/* User info section */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  {user?.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt="Profile"
                      className="w-10 h-10 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                      {avatarLetter}
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-dark">{displayName}</div>
                    <div className="text-xs text-gray-500">{user?.email}</div>
                  </div>
                </div>
              </div>

              <Link
                to="/profile"
                preventScrollReset={true}
                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                onClick={() => setDropdownOpen(false)}
              >
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-3"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  My Profile
                </div>
              </Link>

              <Link
                to="/user/settings"
                preventScrollReset={true}
                className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                onClick={() => setDropdownOpen(false)}
              >
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-3"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  User Settings
                </div>
              </Link>

              {currentOrganization && (
                <Link
                  to={`/org/${currentOrganization.id}/settings`}
                  preventScrollReset={true}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                  onClick={() => setDropdownOpen(false)}
                >
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-3"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Organization Settings
                  </div>
                </Link>
              )}

              <div className="border-t border-gray-100 mt-1"></div>

              <button
                onClick={() => {
                  setDropdownOpen(false);
                  handleLogout();
                }}
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
              >
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-3"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V7.414l-4-4H3zm9 2.586L8.707 9H7a1 1 0 00-1 1v3a1 1 0 001 1h5a1 1 0 001-1v-3a1 1 0 00-1-1H9.414L12 5.586z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Sign Out
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
